import { FormApi } from 'final-form'
import { get, isEmpty, map } from 'lodash'
import moment from 'moment'
import React, { ReactElement } from 'react'
import { Col } from 'react-bootstrap'
import { Field as FinalField } from 'react-final-form'
import { OnChange } from 'react-final-form-listeners'

import Alert from '../Alert'
import { FormRow } from '../Bootstrap'
import DatePicker from '../DatePicker'
import Field, { Readonly, required, useField } from '../Field'
import CronDaySelect from './CronDaySelect'
import CronTimes from './CronTimes'
import CronTypeSelect from './CronTypeSelect'
import { calculateNextExecutions, parseCronConfig } from './helpers'
import { TCron } from './types'
import { generateCronPattern, getNextRun, validateCronPattern } from './utils'

export type TCronConfigurator = {
  name?: string
  form?: FormApi
  disabledTypes?: string[]
  hasEndTime?: boolean
  minDate?: string
}

const CronConfigurator = (props: TCronConfigurator): React.ReactElement => {
  const { name, form, disabledTypes, hasEndTime, minDate } = props

  const firstRunTime = useField(`${name}.firstRunTime`)

  const lastRunTime = useField(`${name}.lastRunTime`)

  const cronType = useField(`${name}.cronType`)

  return (
    <>
      <FormRow>
        <Col md="6">
          <Field
            name={`${name}.cronType`}
            label={i18n.t('cron_type_label', 'Ausführung')}
          >
            <CronTypeSelect disabledTypes={disabledTypes} />
          </Field>
        </Col>

        {cronType !== 'immediately' && (
          <Col md={hasEndTime ? 3 : 6}>
            <Field
              name={`${name}.firstRunTime`}
              required
              validate={required}
              label={
                cronType === 'once'
                  ? i18n.t(`execute_at`, `Ausführen am`)
                  : i18n.t(`start_date`, `Startdatum`)
              }
            >
              <DatePicker block minDate={minDate} />
            </Field>
          </Col>
        )}
        {hasEndTime && cronType !== 'once' && cronType !== 'immediately' && (
          <Col md={3}>
            <Field
              name={`${name}.lastRunTime`}
              label={i18n.t(`end_date`, `Enddatum`)}
            >
              <DatePicker block clearable minDate={firstRunTime} />
            </Field>
          </Col>
        )}
      </FormRow>
      <Readonly name={`${name}`}>
        {(config: TCron): ReactElement => {
          const { cronType } = config

          return (
            <>
              {cronType === 'someDays' && (
                <FormRow>
                  <Col>
                    <Field
                      name={`${name}.cronDays`}
                      label={i18n.t(`weekdays`, `Wochentage`)}
                      required
                      validate={required}
                    >
                      <CronDaySelect isMulti />
                    </Field>
                  </Col>
                </FormRow>
              )}

              {cronType !== 'immediately' && (
                <FormRow>
                  <Col md="6">
                    <CronTimes
                      name={`${name}.cronTimes`}
                      form={form as FormApi}
                      disableSecondChoice={cronType === 'once'}
                    />
                  </Col>
                  {cronType !== 'once' && (
                    <Col md="6">
                      <React.Fragment>
                        <div className="mb-1 mt-2 form-group">
                          <label
                            className="form-label"
                            style={{
                              marginBottom: '2px',
                              opacity: '1',
                              position: 'relative',
                            }}
                          >
                            {i18n.t(
                              'next_5_execution',
                              'Nächste 5 Ausführungen',
                            )}
                          </label>
                        </div>
                        <FinalField
                          name="cronPattern"
                          validate={validateCronPattern(config)}
                        >
                          {(s) => {
                            if (get(s, 'meta.error')) {
                              return (
                                <Alert variant="danger">
                                  {get(s, 'meta.error')}
                                </Alert>
                              )
                            } else {
                              const nextRuns = getNextRun(
                                generateCronPattern(config),
                                (moment(firstRunTime).isSameOrAfter(moment())
                                  ? moment(firstRunTime)
                                  : moment()
                                )
                                  .startOf('day')
                                  .toISOString(),
                                5,
                                get(config, 'cronTimes', []),
                                lastRunTime
                                  ? moment(lastRunTime)
                                      .endOf('day')
                                      .toISOString()
                                  : null,
                              )
                              return isEmpty(nextRuns) ? (
                                <div>
                                  {i18n.t(
                                    'no_next_cron_executions',
                                    'Keine weiteren Ausführungen',
                                  )}
                                </div>
                              ) : (
                                map(nextRuns || [], (run) => <div>{run}</div>)
                              )
                            }
                          }}
                        </FinalField>
                      </React.Fragment>
                    </Col>
                  )}
                </FormRow>
              )}
            </>
          )
        }}
      </Readonly>
      <OnChange name={`${name}.cronType`}>
        {(value, previousValue) => {
          if (!form) {
            return
          }
          if (previousValue) {
            if (value !== 'someDays') {
              form.change(`${name}.cronDays`, [])
            }
            form.change(`${name}.cronTimes`, ['00:00'])
          }
        }}
      </OnChange>

      <OnChange name={`${name}`}>
        {(value) => {
          if (form) {
            form.change('cronPattern', generateCronPattern(value))
          }
        }}
      </OnChange>
    </>
  )
}

export default CronConfigurator

export { calculateNextExecutions, parseCronConfig }
